/**
 * AgentSurface — the conversational clarification surface on /compare.
 *
 * Renders IMMEDIATELY (no server-side block). On mount it fires the
 * first agent turn from the browser, showing a typing indicator
 * while it waits. The full conversation lives on this one page —
 * chip clicks send the NEXT agent turn (not a search). Only when
 * the agent itself returns action="search" do we navigate to the
 * existing /compare streaming surface.
 *
 * Day 11 rewrite (2026-06-16):
 *   - Page paints in < 100 ms; agent runs client-side.
 *   - Chip click = append user message + fire next turn (NEVER
 *     navigates straight to a search).
 *   - Only action="search" navigates to /compare?q=X&from_chip=1.
 *   - 503 from the agent endpoint (kill switch off) → fall back to
 *     immediate streaming search with the raw query.
 *
 * Architecture:
 *   /compare server component renders <AgentSurface query={raw_query}
 *   postcode={postcode} /> when the user did NOT just click a chip
 *   (fromChip=1). AgentSurface owns the conversation; ComparisonStream
 *   handles the actual paid search once the agent says we're ready.
 */

"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { Route } from "next";
import { useRouter } from "next/navigation";

import {
  AgentTurnError,
  postAgentTurn,
  type AgentTurnMessage,
} from "@/lib/api/agent-turn";

import { InputDock } from "./InputDock";
import { Thread } from "./Thread";
import type { Chip, ConversationMessage } from "./types";


export interface AgentSurfaceProps {
  /** The raw user query that landed them on /compare — typically the
   *  homepage submit. The first agent turn fires automatically with
   *  this as the user message. */
  query: string;
  /** Postcode forwarded to /compare when the agent eventually
   *  dispatches a search. Never sent to the LLM. */
  postcode: string;
}

// Day 11 fix (2026-06-16): bound the payload to the last N messages
// before sending. The backend caps at 30; we keep a comfortable
// margin so a runaway user can keep clicking without ever 422-ing.
// The LLM doesn't need ancient context — pivot rule says "latest
// message wins" — and keeping payload bounded keeps cost predictable.
const MAX_API_MESSAGES = 16;

function toApiMessages(messages: ConversationMessage[]): AgentTurnMessage[] {
  const tail = messages.slice(-MAX_API_MESSAGES);
  return tail.map((m) =>
    m.role === "user"
      ? { role: "user" as const, content: m.content }
      : { role: "assistant" as const, content: m.message },
  );
}

function navigateToSearch(
  router: ReturnType<typeof useRouter>,
  searchQuery: string,
  postcode: string,
): void {
  const href =
    `/compare?q=${encodeURIComponent(searchQuery)}&postcode=${encodeURIComponent(postcode)}&from_chip=1` as Route;
  router.push(href);
}

export function AgentSurface({ query, postcode }: AgentSurfaceProps) {
  const router = useRouter();

  // Seed the conversation with the user's first message so it renders
  // immediately in the thread. The assistant reply lands when the
  // first agent turn returns.
  const [messages, setMessages] = useState<ConversationMessage[]>([
    { role: "user", content: query },
  ]);
  const [isLoading, setIsLoading] = useState(true);

  // Guard so the first-turn fire-on-mount is React-StrictMode safe
  // (StrictMode double-invokes effects in dev).
  const firstTurnFiredRef = useRef(false);

  /**
   * Send a user message to the agent and append its reply. If the
   * agent decides action=search, navigate to the streaming /compare
   * surface; otherwise append the assistant turn so the user can
   * keep clicking chips or typing.
   *
   * `nextMessages` includes the new user message — the caller has
   * already appended it to local state before calling this.
   */
  const runTurn = useCallback(
    async (nextMessages: ConversationMessage[]) => {
      setIsLoading(true);
      try {
        const result = await postAgentTurn({
          messages: toApiMessages(nextMessages),
          country_code: "AU",
          postcode,
        });

        if (result.action === "search" && result.search_query) {
          // Agent decided we're narrow enough. Hand off to streaming
          // search. Conversation ends here.
          navigateToSearch(router, result.search_query, postcode);
          return;
        }

        // action=clarify — append the assistant turn and keep going.
        setMessages([
          ...nextMessages,
          {
            role: "assistant",
            acknowledgment: result.acknowledgment ?? "",
            message: result.message ?? "",
            chips: result.chips ?? [],
            turn_id: result.turn_id,
          },
        ]);
      } catch (err) {
        // Day 11 graceful degradation (2026-06-16): on ANY agent
        // failure, take the user's latest message and fire the search
        // directly. Better UX than a "hiccup" dead-end:
        //   - 503 (kill switch off) → search fires (as before)
        //   - 422 (validation) → search fires with last message
        //   - 500 (LLM/runtime error) → search fires
        //   - Network / timeout → search fires
        // The user typed something. They want results. Give results.
        const lastUserMessage =
          [...nextMessages].reverse().find((m) => m.role === "user");
        const fallbackQuery =
          lastUserMessage?.role === "user"
            ? lastUserMessage.content
            : query;
        // Defensive: only navigate when we actually have a non-empty
        // query to search. Empty query would render /compare's empty
        // state.
        if (fallbackQuery && fallbackQuery.trim() !== "") {
          navigateToSearch(router, fallbackQuery.trim(), postcode);
          return;
        }
        // Last-ditch: surface the failure inline if we somehow have
        // nothing to fall back on. Should be unreachable.
        const status =
          err instanceof AgentTurnError ? `(${err.status})` : "";
        setMessages([
          ...nextMessages,
          {
            role: "assistant",
            acknowledgment: "",
            message: `Couldn't reach Octopusden right now ${status}. Try refreshing the page.`.trim(),
            chips: [],
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [postcode, query, router],
  );

  // Fire the first agent turn on mount. React StrictMode double-
  // invokes this in dev; the ref guard prevents two API calls.
  useEffect(() => {
    if (firstTurnFiredRef.current) return;
    firstTurnFiredRef.current = true;
    void runTurn([{ role: "user", content: query }]);
    // Run exactly once on mount with the seeded query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * User types refinement / pivot in the InputDock.
   */
  const handleUserSubmit = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;
      const next: ConversationMessage[] = [
        ...messages,
        { role: "user", content: trimmed },
      ];
      setMessages(next);
      await runTurn(next);
    },
    [messages, isLoading, runTurn],
  );

  /**
   * User clicks a chip. Treat the chip as a new user message in the
   * conversation — NOT a direct navigation to search. The agent
   * decides whether to keep clarifying or dispatch.
   */
  const handleChipClick = useCallback(
    async (chip: Chip) => {
      if (isLoading) return;
      // Append the chip's user-facing label as the user message so
      // the thread reads naturally ("User: Front loader 7-9 kg"
      // not "User: Samsung front load washing machine 7 to 9 kg").
      const next: ConversationMessage[] = [
        ...messages,
        { role: "user", content: chip.label },
      ];
      setMessages(next);
      await runTurn(next);
    },
    [messages, isLoading, runTurn],
  );

  return (
    <main
      className="relative min-h-screen w-full"
      style={{ minHeight: "100svh", paddingBottom: 180 }}
    >
      <div style={{ paddingTop: 56 }}>
        <Thread
          messages={messages}
          isLoading={isLoading}
          onChipClick={handleChipClick}
        />
      </div>
      <InputDock onSubmit={handleUserSubmit} disabled={isLoading} />
    </main>
  );
}
